import type { SupabaseClient } from "@supabase/supabase-js";
import { parseNullableNumber } from "./coach";

/**
 * Client-side helpers for the coach Inbox surface. Backed by the new
 * `coach_assignment_replies` table — one row per student reply to a
 * coach assignment/cue. Coaches read + mark-read; students (owners)
 * insert via the iOS app. RLS prevents either side from reading the
 * other's workspace.
 */

export interface CoachAssignmentReply {
  id: string;
  assignmentId: string;
  collaborationId: string;
  authorUserId: string;
  body: string;
  audioPath: string | null;
  audioDurationSeconds: number | null;
  readAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboxThreadRow {
  assignmentId: string;
  assignmentTitle: string;
  assignmentIsCue: boolean;
  collaborationId: string;
  collaborationTitle: string;
  studentEmail: string | null;
  replyCount: number;
  unreadCount: number;
  latestReplyBody: string;
  latestReplyAt: string;
}

const REPLY_COLUMNS =
  "id,assignment_id,collaboration_id,author_user_id,body,audio_path,audio_duration_seconds,read_at,created_at,updated_at";

/**
 * One row per assignment that has at least one student reply, sorted
 * by most-recent reply. Joins into `coach_assignments` +
 * `coach_collaborations` so the Inbox card can render the assignment
 * title and student label without a second fetch.
 */
export async function listInboxThreads(
  client: SupabaseClient,
): Promise<InboxThreadRow[]> {
  const { data, error } = await client
    .from("coach_assignment_replies")
    .select(
      `id,
       assignment_id,
       collaboration_id,
       body,
       read_at,
       created_at,
       coach_assignments:assignment_id ( title, is_cue ),
       coach_collaborations:collaboration_id ( title, owner_email )`,
    )
    .order("created_at", { ascending: false });
  if (error) throw error;

  const byAssignment = new Map<string, InboxThreadRow>();
  for (const raw of (data ?? []) as Array<Record<string, unknown>>) {
    const assignmentId = String(raw.assignment_id);
    const assignmentJoin = (raw.coach_assignments as
      | { title?: string; is_cue?: boolean }
      | null) ?? null;
    const collaborationJoin = (raw.coach_collaborations as
      | { title?: string; owner_email?: string | null }
      | null) ?? null;
    const createdAt = String(raw.created_at ?? "");
    const readAt = raw.read_at as string | null | undefined;

    const existing = byAssignment.get(assignmentId);
    if (!existing) {
      byAssignment.set(assignmentId, {
        assignmentId,
        assignmentTitle: assignmentJoin?.title ?? "Assignment",
        assignmentIsCue: Boolean(assignmentJoin?.is_cue ?? false),
        collaborationId: String(raw.collaboration_id),
        collaborationTitle: collaborationJoin?.title ?? "Workspace",
        studentEmail: collaborationJoin?.owner_email ?? null,
        replyCount: 1,
        unreadCount: readAt ? 0 : 1,
        latestReplyBody: String(raw.body ?? ""),
        latestReplyAt: createdAt,
      });
    } else {
      existing.replyCount += 1;
      if (!readAt) existing.unreadCount += 1;
    }
  }
  return Array.from(byAssignment.values()).sort((a, b) =>
    a.latestReplyAt < b.latestReplyAt ? 1 : -1,
  );
}

/**
 * All replies on a single assignment, oldest-first, for thread view.
 */
export async function listAssignmentReplies(
  client: SupabaseClient,
  assignmentId: string,
): Promise<CoachAssignmentReply[]> {
  const { data, error } = await client
    .from("coach_assignment_replies")
    .select(REPLY_COLUMNS)
    .eq("assignment_id", assignmentId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []).map(mapReply);
}

/**
 * Mark every unread reply on an assignment as read for the calling
 * coach. Uses a single UPDATE so mark-on-open is one round-trip.
 */
export async function markAssignmentRepliesRead(
  client: SupabaseClient,
  assignmentId: string,
): Promise<number> {
  const nowIso = new Date().toISOString();
  const { data, error } = await client
    .from("coach_assignment_replies")
    .update({ read_at: nowIso })
    .eq("assignment_id", assignmentId)
    .is("read_at", null)
    .select("id");
  if (error) throw error;
  return (data ?? []).length;
}

/**
 * Aggregate unread count across all of the coach's inbox threads.
 * Used by the tab bar badge. One count() round-trip, no join.
 */
export async function countUnreadReplies(
  client: SupabaseClient,
): Promise<number> {
  const { count, error } = await client
    .from("coach_assignment_replies")
    .select("id", { count: "exact", head: true })
    .is("read_at", null);
  if (error) throw error;
  return count ?? 0;
}

export type InboxRealtimeStatus =
  | "connecting"
  | "subscribed"
  | "error"
  | "timed_out"
  | "closed";

/**
 * Subscribe to realtime INSERT events on `coach_assignment_replies`.
 * The callback fires once per new reply (row object passed through).
 * Returns an unsubscribe function the caller must invoke on unmount.
 *
 * Requires the table to be attached to the `supabase_realtime`
 * publication — see migration
 * `20260421000600_enable_inbox_realtime.sql`. When the migration is
 * missing (e.g. not yet deployed, or realtime disabled on the
 * project), the subscription will never reach `subscribed` — that's
 * surfaced through `onStatusChange` so callers can swap in a polling
 * fallback instead of staying silent.
 *
 * RLS still applies on the realtime channel: each client only
 * receives INSERTs on rows they would be allowed to SELECT, so a
 * coach never sees another coach's replies flow by.
 */
export function subscribeToInboxReplies(
  client: SupabaseClient,
  onReply: (row: Record<string, unknown>) => void,
  onStatusChange?: (status: InboxRealtimeStatus) => void,
): () => void {
  onStatusChange?.("connecting");
  const channel = client
    .channel("coach-inbox-realtime")
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "coach_assignment_replies",
      },
      (payload) => {
        onReply((payload.new as Record<string, unknown>) ?? {});
      },
    )
    .subscribe((rawStatus) => {
      onStatusChange?.(mapRealtimeStatus(rawStatus));
    });
  return () => {
    void client.removeChannel(channel);
  };
}

/**
 * Map Supabase's realtime status strings to our narrower enum so
 * callers don't need to depend on the SDK's internal constants.
 * Anything not recognised is treated as "closed" (silent fallback).
 */
export function mapRealtimeStatus(raw: string): InboxRealtimeStatus {
  switch (raw) {
    case "SUBSCRIBED":
      return "subscribed";
    case "CHANNEL_ERROR":
      return "error";
    case "TIMED_OUT":
      return "timed_out";
    case "CLOSED":
      return "closed";
    default:
      return "connecting";
  }
}

function mapReply(row: Record<string, unknown>): CoachAssignmentReply {
  return {
    id: String(row.id),
    assignmentId: String(row.assignment_id),
    collaborationId: String(row.collaboration_id),
    authorUserId: String(row.author_user_id),
    body: String(row.body ?? ""),
    audioPath: (row.audio_path as string | null) ?? null,
    audioDurationSeconds: parseNullableNumber(row.audio_duration_seconds),
    readAt: (row.read_at as string | null) ?? null,
    createdAt: String(row.created_at ?? ""),
    updatedAt: String(row.updated_at ?? ""),
  };
}
